import { CheckIcon } from '@assets/icons';
import { useTranslation } from '@shared/core';
import { ButtonV2, DialogV2, Flex, TextV2 } from '@withjoy/joykit';
import React, { useEffect, useRef, useState } from 'react';

type ShareableDialogProps = Readonly<{
  isOpen: boolean;
  onClose: () => void;
  onCopy?: (generatedLink: string) => void;
  generateShareableLink?: () => Promise<string | null>;
  onCancelClick?: () => void;
  onConfirmClick?: () => void;
}>;

const ShareableDialog: React.FC<ShareableDialogProps> = ({ isOpen, onClose, onCopy, generateShareableLink, onConfirmClick, onCancelClick }) => {
  const { tq } = useTranslation('emailsAndEcards');

  const [generatedLink, setGeneratedLink] = useState<string | null>(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [hasFailed, setHasFailed] = useState(false);

  // The dialog stays mounted, so closing must reset it or the next open inherits the old state.
  const openTokenRef = useRef(0);
  useEffect(() => {
    if (isOpen) return;
    openTokenRef.current += 1;
    setHasFailed(false);
    setIsGenerating(false);
    // The next open may be for a different card, so the link cannot be reused.
    setGeneratedLink(null);
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen || !generateShareableLink || generatedLink !== null || isGenerating || hasFailed) {
      return;
    }

    // Not effect cleanup: `generateShareableLink` changes identity every render, which would cancel this.
    const token = openTokenRef.current;
    const generate = async () => {
      setIsGenerating(true);
      const link = await generateShareableLink().catch(() => null);
      if (token !== openTokenRef.current) return;
      if (link) setGeneratedLink(link);
      else setHasFailed(true);
      setIsGenerating(false);
    };

    generate();
  }, [isOpen, generateShareableLink, generatedLink, isGenerating, hasFailed]);

  const handleCopyButtonClick = () => {
    if (hasFailed) {
      setHasFailed(false);
      return;
    }
    onCopy?.(generatedLink ?? '');
    onClose();
    onConfirmClick?.();
  };

  const handleCancelClick = () => {
    onCancelClick?.();
    onClose();
  };

  return (
    <DialogV2
      isOpen={isOpen}
      onClose={onClose}
      size="lg"
    >
      <DialogV2.Header>
        <DialogV2.CloseButton />
      </DialogV2.Header>
      <DialogV2.Body>
        <TextV2
          typographyVariant="hed5"
          textAlign="center"
          marginBottom={6}
          marginTop={['3rem', null, 0]}
        >
          {tq`About Shareable Card Links`}
        </TextV2>
        <Flex
          paddingBottom={5}
          marginBottom={5}
          borderBottom="1px solid rgba(235, 235, 235, 1)"
        >
          <Flex
            background="#333"
            padding="6px"
            width={6}
            height={6}
            alignItems="center"
            justifyItems="center"
            borderRadius="12px"
            flex="none"
            marginRight={5}
            color="white"
          >
            <CheckIcon />
          </Flex>
          <TextV2>{tq`Unlike cards sent via email, cards shared via link will not be addressed to a specific guest or tracked on your guest list.`}</TextV2>
        </Flex>
        <Flex
          paddingBottom={5}
          marginBottom={5}
          borderBottom="1px solid rgba(235, 235, 235, 1)"
        >
          <Flex
            background="#333"
            padding="6px"
            width={6}
            height={6}
            alignItems="center"
            justifyItems="center"
            borderRadius="12px"
            flex="none"
            marginRight={5}
            color="white"
          >
            <CheckIcon />
          </Flex>
          <Flex flexDirection="column">
            <TextV2>{tq`Card contents will be visible to anyone with the link, regardless of your event privacy settings.`}</TextV2>
            <TextV2 typographyVariant="label2">{tq`This includes your event date, location, and website password (if you have included it on the card).`}</TextV2>
          </Flex>
        </Flex>
        <Flex paddingBottom={5}>
          <Flex
            background="#333"
            padding="6px"
            width={6}
            height={6}
            alignItems="center"
            justifyItems="center"
            borderRadius="12px"
            flex="none"
            marginRight={5}
            color="white"
          >
            <CheckIcon />
          </Flex>
          <TextV2>{tq`Contents of your website, including schedule visibility and RSVP, will continue to be protected by your existing privacy settings.`}</TextV2>
        </Flex>
      </DialogV2.Body>
      {hasFailed && (
        <TextV2
          typographyVariant="label2"
          textAlign="center"
          color="negative6"
          paddingX={5}
        >
          {tq`Couldn't create the link. Try again.`}
        </TextV2>
      )}
      <DialogV2.Footer flexDirection={['column-reverse', null, 'row']}>
        <ButtonV2
          width={['100%', null, 'auto']}
          intent="neutral"
          variant="ghost"
          onClick={handleCancelClick}
        >
          {tq`Cancel`}
        </ButtonV2>
        <ButtonV2
          width={['100%', null, 'auto']}
          marginBottom={[4, null, 0]}
          intent="neutral"
          onClick={handleCopyButtonClick}
          loading={isGenerating}
          disabled={!!generateShareableLink && !hasFailed && !generatedLink}
        >
          {hasFailed ? tq`Try Again` : tq`Copy Link`}
        </ButtonV2>
      </DialogV2.Footer>
    </DialogV2>
  );
};

export default ShareableDialog;
